import {
  Alert,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { EmailAccountInfo } from "../../../api/emailAccountsTypes";
import { api } from "../../../api/operationsApi";
import UserContext from "../../../context/UserContext";
import EmailAccountCells from "./EmailAccountCells";
import EmailAccountHeaderCells from "./EmailAccountHeaderCells";
import { sendingProblemLabels } from "./emailAccountsLabels";
import { getSmtpLoginStatus } from "./smtpSettings";
import AddEmailAccountDialog from "./AddEmailAccountDialog";
import DeleteEmailAccountDialog from "./DeleteEmailAccountDialog";
import ResetSmtpPasswordDialog from "./ResetSmtpPasswordDialog";
import SendTestEmailDialog from "./SendTestEmailDialog";

interface Props {
  clientCode: string;
}

const ClientEmailAccounts = ({ clientCode }: Props) => {
  const { permissions } = useDefinedContext(UserContext);
  const [testEmailAccount, setTestEmailAccount] = useState<EmailAccountInfo>();
  const [resetPasswordAccount, setResetPasswordAccount] = useState<EmailAccountInfo>();
  const [deleteAccount, setDeleteAccount] = useState<EmailAccountInfo>();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const getEmailAccounts = useCallback(() => api.emailAccounts.getClientEmailAccounts(clientCode), [clientCode]);
  const [emailAccounts, fetchError, { fetch: refreshEmailAccounts }] = useFetch(getEmailAccounts);
  const getSmtpLogins = useCallback(() => api.emailAccounts.getClientSmtpLogins(clientCode), [clientCode]);
  const [smtpLogins, smtpLoginsError, { fetch: refreshSmtpLogins, setData: setSmtpLogins }] = useFetch(getSmtpLogins);

  if (fetchError) {
    logError(fetchError, "[ClientEmailAccounts] getClientEmailAccounts");
    return <DataLoadingFailed text={fetchError.message} />;
  }

  if (emailAccounts === undefined) {
    return <InlineLoader />;
  }

  if (smtpLoginsError) {
    logError(smtpLoginsError, "[ClientEmailAccounts] getClientSmtpLogins");
  }

  const canManage = permissions.includes("ManageClientEmailAccounts");
  const getStatus = (accountId: string) => getSmtpLoginStatus(smtpLogins, smtpLoginsError !== undefined, accountId);
  const refresh = () => {
    setSmtpLogins(undefined);
    void refreshEmailAccounts();
    void refreshSmtpLogins();
  };

  return (
    <Stack spacing={1}>
      {canManage && (
        <Stack direction="row">
          <Button variant="outlined" onClick={() => setIsAddDialogOpen(true)}>
            Add email account
          </Button>
        </Stack>
      )}
      {emailAccounts.sendingProblem && emailAccounts.accounts.length > 0 && (
        <Alert severity="warning">This client cannot send: {sendingProblemLabels[emailAccounts.sendingProblem]}.</Alert>
      )}
      {emailAccounts.accounts.length === 0 ? (
        <Typography color="textSecondary">No email accounts are assigned to this client.</Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <EmailAccountHeaderCells />
              <TableCell />
              {canManage && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {emailAccounts.accounts.map((account) => (
              <TableRow key={account.id}>
                <EmailAccountCells account={account} clientCode={clientCode} smtpLoginStatus={getStatus(account.id)} />
                <TableCell>{account.id === emailAccounts.defaultAccountId && <Chip label="Default" />}</TableCell>
                {canManage && (
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" onClick={() => setTestEmailAccount(account)}>
                        Send test email
                      </Button>
                      {getStatus(account.id) === "Present" && (
                        <Button variant="outlined" color="warning" onClick={() => setResetPasswordAccount(account)}>
                          Reset SMTP password
                        </Button>
                      )}
                      <Button variant="outlined" color="error" onClick={() => setDeleteAccount(account)}>
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {isAddDialogOpen && (
        <AddEmailAccountDialog
          clientCode={clientCode}
          onClose={(created) => {
            setIsAddDialogOpen(false);
            if (created) {
              refresh();
            }
          }}
        />
      )}
      {testEmailAccount && (
        <SendTestEmailDialog
          account={testEmailAccount}
          clientCodes={[clientCode]}
          getClientTitle={(code) => code}
          onClose={() => setTestEmailAccount(undefined)}
        />
      )}
      {resetPasswordAccount && (
        <ResetSmtpPasswordDialog
          clientCode={clientCode}
          account={resetPasswordAccount}
          onClose={() => setResetPasswordAccount(undefined)}
        />
      )}
      {deleteAccount && (
        <DeleteEmailAccountDialog
          clientCode={clientCode}
          account={deleteAccount}
          clientAccounts={emailAccounts.accounts}
          defaultAccountId={emailAccounts.defaultAccountId}
          smtpLoginStatus={getStatus(deleteAccount.id)}
          onClose={(wasAttempted) => {
            setDeleteAccount(undefined);
            if (wasAttempted) {
              refresh();
            }
          }}
        />
      )}
    </Stack>
  );
};

export default ClientEmailAccounts;
